import { useCallback, useMemo, useState } from 'react';
import { ExternalListingRow_ExternalListingFragment, ListingsSection_AdFragment } from '../../../../codegen/graphql';
import { createListingConfigurationWithDefaults } from '../../../../common/listingVariationsConfiguration';
import SetMarginOrTargetPrice from '../../common/SetMarginOrTargetPrice';
import { Modal } from '../../common/Modal';

type MarginUpdate = {
  marginPercentOnBaseCost?: number | null;
  targetListingPriceIncludingVat?: number | null;
  listingPriceRoundingMethod?: import('../../../../codegen/graphql').PriceRoundingMethod | null;
};

interface BulkEditListingMarginModalProps {
  adData: ListingsSection_AdFragment;
  selectedListings: ExternalListingRow_ExternalListingFragment[];
  onClose: () => void;
  onApply: (update: MarginUpdate) => Promise<void>;
}

export function BulkEditListingMarginModal({
  adData,
  selectedListings,
  onClose,
  onApply,
}: BulkEditListingMarginModalProps) {
  const [marginUpdate, setMarginUpdate] = useState<MarginUpdate>({});
  const [isApplying, setIsApplying] = useState(false);

  const referenceListing = selectedListings[0];

  const listingConfigurationWithDefaults = useMemo(() => {
    if (!referenceListing) return null;
    return createListingConfigurationWithDefaults(
      referenceListing.configuration,
      adData.variationTypes,
      adData.variations,
    );
  }, [referenceListing, adData.variationTypes, adData.variations]);

  const handleUpdate = useCallback((update: MarginUpdate) => {
    setMarginUpdate((prev) => ({ ...prev, ...update }));
  }, []);

  const handleApply = useCallback(async () => {
    setIsApplying(true);
    try {
      await onApply(marginUpdate);
      onClose();
    } finally {
      setIsApplying(false);
    }
  }, [marginUpdate, onApply, onClose]);

  if (!referenceListing || !listingConfigurationWithDefaults) {
    return null;
  }

  return (
    <Modal
      title={`Edit margin for ${selectedListings.length} listing(s)`}
      onClose={onClose}
      width="medium"
      actions={[
        {
          children: 'Cancel',
          preset: 'white',
          onClick: onClose,
        },
        {
          children: 'Apply',
          preset: 'primary',
          onClick: handleApply,
          disabled: isApplying,
        },
      ]}
    >
      <p className="text-sm text-gray-600 mb-4">
        Margin settings will be applied to all selected listings.
      </p>
      <SetMarginOrTargetPrice
        setForKey={`listing-${referenceListing.id}`}
        data={{
          listing: referenceListing,
          listingConfiguration: listingConfigurationWithDefaults,
          variation: null,
          ad: adData,
        }}
        onUpdate={handleUpdate}
      />
    </Modal>
  );
}
